// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
// eslint-disable-next-line import/no-extraneous-dependencies
import { createFilter } from 'react-select';

import type { FavoriteContact } from '@gandi/redux-resource-contactbook';
import { useTranslator } from '@gandi/translate';

import { Base, Button, Label, Checkbox } from '@gandi/ui';

import { Custom } from '@gandi/ui-select';

import type { Params, Query } from 'src/helpers/withRouter';
import { useAppSelector } from 'src/hooks/useAppSelector';
import CountryLoader from 'src/shared/CountryLoader';
import type { PreFetchArgs, PreFetchReturn, FullState } from 'src/types';

import { saveDraftContact } from '../../../modules/actions';
import { getSetupOverviewUrl } from '../../../modules/helpers';
import { selectHasDomainTransfer } from '../../../modules/selectors';
import type { CreateOrTransfer, EditableRole } from '../../../types';

import InvitationCheckboxWrapper from '../InvitationCheckboxWrapper';

import ContactDetails from './components/ContactDetails';

export type OwnProps = {
  process: CreateOrTransfer,
  role: EditableRole,
  contacts: Array<FavoriteContact>,
};

export type Props = {
  ...OwnProps,
};

export type State = { selectedContact?: FavoriteContact };

const getId = (contact: FavoriteContact) => contact.id;

const stringify = ({ data }: any): string =>
  Object.keys(data)
    .filter(key => data[key] && typeof data[key] === 'string')
    .reduce((acc, key) => {
      // default case
      let substring = data[key];

      // Handle firstname / lastname semantic groups
      if (key === 'firstname') {
        substring = `${acc}${data.firstname} ${data.lastname}`;
      }
      if (key === 'lastname') {
        substring = `${acc}${data.lastname} ${data.firstname}`;
      }

      return `${acc}${substring} `;
    }, '');

const filterOption = createFilter({ stringify });

const Presenter = ({ contacts, process, role }: Props): React.Node => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { cartId } = useParams<{
    cartId: string,
    process: CreateOrTransfer,
    role: EditableRole,
  }>();
  const [selectedContact, setSelectedContact] =
    React.useState<FavoriteContact | void>();

  const [sendInvitation, setSendInvitation] = React.useState<boolean>(false);

  const translator = useTranslator();

  const { __ } = translator;
  const cancelUrl = getSetupOverviewUrl(cartId, process);

  const hasDomainTransfer = useAppSelector(selectHasDomainTransfer);

  const onSubmit = async () => {
    const contact = selectedContact
      ? {
          ...selectedContact,
          send_invitation: sendInvitation,
        }
      : undefined;
    if (!contact) {
      return;
    }

    dispatch(
      saveDraftContact({
        process,
        contact,
        role,
      }),
    );

    navigate(getSetupOverviewUrl(cartId, process));
  };
  // TODO: Use reshaped
  return (
    <>
      <CountryLoader />
      <Base marginBottom="base" className="wrapper-md">
        <Base marginBottom="xsmall">
          <Label target="contactSelect" required>
            {__('Select contact')}
          </Label>
          <Custom
            inputId="contactSelect"
            name="contactSelect"
            options={contacts}
            translator={translator}
            onChange={(favoriteContact: FavoriteContact) => {
              setSelectedContact(favoriteContact);
            }}
            getOptionValue={getId}
            filterOption={filterOption}
            renderOption={option => <ContactDetails contact={option} />}
          />
        </Base>
      </Base>
      {hasDomainTransfer && (
        <Base marginBottom="base">
          <InvitationCheckboxWrapper>
            <Checkbox
              name="send-invitation"
              onChange={evt => {
                setSendInvitation(evt.target.checked);
              }}
              checked={sendInvitation}
            >
              {__(
                'Email an invitation to the contact, allowing them to manage the domain.',
              )}
            </Checkbox>
          </InvitationCheckboxWrapper>
        </Base>
      )}

      <Base marginBottom="base">
        <Button kind="outline" to={cancelUrl} grouped>
          {__('Cancel')}
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={!selectedContact}
          grouped
        >
          {__('Apply')}
        </Button>
      </Base>
    </>
  );
};

Presenter.preFetch = (
  props: PreFetchArgs<{}, Params, Query, FullState>,
): PreFetchReturn<> => CountryLoader.preFetch(props);

Presenter.displayName = 'SelectFromContactBook';
export default Presenter;
